import { useEffect, useMemo, useState } from 'react';
import { ApiError, api, useQuery } from '../api.js';
import { Drawer, Pill, useToast } from '../ui.js';
import type { Slot } from '../../../shared/types.js';

interface RequestData {
  student: { id: number };
  nextLesson: { id: number; code: string; name: string; flightHours: number; groundHours: number } | null;
  gate: { title: string; body: string } | null;
  instructors: { id: number; name: string }[];
  aircraft: { id: number; tail: string; model: string }[];
  readOnly: boolean;
}

const WHEN = ['Morning', 'Midday', 'Afternoon', 'Evening'];

/**
 * Asking for a lesson. Opened from the schedule tab rather than sitting on it,
 * so the portal opens on what is already booked instead of on a form.
 */
export function PortalRequest({ data, instructorName, onClose, onSent }: {
  data: RequestData;
  instructorName: string;
  onClose: () => void;
  onSent: () => void;
}): JSX.Element {
  const toast = useToast();

  const [instructorId, setInstructorId] = useState<number | null>(null);
  const [aircraftId, setAircraftId] = useState<number | null>(null);
  const [partOfDay, setPartOfDay] = useState('Morning');
  const [day, setDay] = useState<string | null>(null);
  const [picked, setPicked] = useState<Slot | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const lessonId = data.nextLesson?.id ?? null;
  const duration = data.nextLesson ? Math.max(1, Math.round((data.nextLesson.flightHours + 0.4) * 2) / 2) : 2;

  const query = (extra: Record<string, string>) => {
    const q = new URLSearchParams({ durationHours: String(duration), partOfDay, ...extra });
    if (lessonId) q.set('lessonId', String(lessonId));
    if (instructorId) q.set('instructorId', String(instructorId));
    if (aircraftId) q.set('aircraftId', String(aircraftId));
    return q.toString();
  };

  const daysPath = useMemo(
    () => `/portal/days?${query({})}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [duration, partOfDay, lessonId, instructorId, aircraftId],
  );
  const days = useQuery<{ days: { date: string; dow: string; label: string; open: number }[] }>(daysPath);

  const slotsPath = useMemo(
    () => (day ? `/portal/slots?${query({ date: day })}` : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [day, duration, partOfDay, lessonId, instructorId, aircraftId],
  );
  const slots = useQuery<{ slots: Slot[] }>(slotsPath);

  useEffect(() => { setPicked(null); }, [day, partOfDay, instructorId, aircraftId]);
  useEffect(() => {
    // Land on the first day that actually has an opening.
    if (!day && days.data?.days.length) {
      setDay((days.data.days.find((d) => d.open > 0) ?? days.data.days[0]).date);
    }
  }, [days.data, day]);

  const send = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await api.post('/portal/requests', {
        startsAt: picked.startsAt, endsAt: picked.endsAt,
        aircraftId: picked.aircraftId, instructorId: picked.instructorId,
        lessonId, notes: note,
      });
      toast.ok('Request sent', 'Your slot is held for 12 hours while your instructor confirms.');
      onSent();
    } catch (err) {
      if (err instanceof ApiError && err.findings?.length) {
        toast.error('That time is no longer available', err.findings.map((f) => f.title).join(' \u00b7 '));
      } else {
        toast.error('Could not send that request', err instanceof Error ? err.message : undefined);
      }
      slots.reload();
      setBusy(false);
    }
  };

  return (
    <Drawer
      title="Request a lesson"
      subtitle={data.nextLesson ? `${data.nextLesson.code} \u00b7 ${data.nextLesson.name}` : 'Course complete'}
      onClose={onClose}
      footer={
        <>
          <span className="muted" style={{ fontSize: 13, flex: 1 }}>
            {picked
              ? `${new Date(picked.startsAt).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })} at ${new Date(picked.startsAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`
              : 'No time selected yet'}
          </span>
          <button className="btn btn--primary btn--lg" disabled={!picked || busy || data.readOnly} onClick={send}>
            Send request
          </button>
        </>
      }
    >
      <div className="portal-request">

        {data.gate && (
          <div style={{ margin: 18, padding: 10, background: 'var(--pending-bg)', border: '1px solid var(--pending-bd)', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 12.5, color: 'var(--pending-c)' }}>{data.gate.title}</div>
            <div className="small" style={{ color: 'var(--pending-c)', marginTop: 2, textWrap: 'pretty' }}>{data.gate.body}</div>
          </div>
        )}

        <div style={{ padding: '16px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12, borderBottom: '1px solid var(--border-3)' }}>
          <label className="field">
            <span>Lesson</span>
            <input className="input" style={{ padding: '9px 11px', borderRadius: 10 }} readOnly
              value={data.nextLesson ? `${data.nextLesson.code} · ${data.nextLesson.name}` : 'Course complete'} />
          </label>
          <label className="field">
            <span>Instructor</span>
            <select className="select" style={{ padding: '9px 11px', borderRadius: 10 }}
              value={instructorId ?? ''} onChange={(e) => setInstructorId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Any available instructor</option>
              {data.instructors.map((i) => (
                <option key={i.id} value={i.id}>{i.name}{i.name === instructorName ? ' (my instructor)' : ''}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Aircraft preference</span>
            <select className="select" style={{ padding: '9px 11px', borderRadius: 10 }}
              value={aircraftId ?? ''} onChange={(e) => setAircraftId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Any aircraft</option>
              {data.aircraft.map((a) => <option key={a.id} value={a.id}>{a.tail} · {a.model}</option>)}
            </select>
          </label>
        </div>

        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border-3)' }}>
          <div className="small muted" style={{ marginBottom: 8 }}>Pick a day</div>
          <div className="row" style={{ gap: 8 }}>
            {days.loading && !days.data && <span className="small muted">Checking the fleet…</span>}
            {days.data?.days.map((d) => (
              <button
                key={d.date}
                disabled={d.open === 0}
                onClick={() => setDay(d.date)}
                style={{
                  padding: '9px 12px', minWidth: 86, minHeight: 58, textAlign: 'left', borderRadius: 10,
                  border: `1px solid ${d.date === day ? 'var(--accent)' : 'var(--border)'}`,
                  background: d.date === day ? 'var(--accent-tint)' : 'var(--surface)',
                  opacity: d.open === 0 ? 0.6 : 1,
                }}
              >
                <span className="small muted" style={{ display: 'block' }}>{d.dow}</span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{d.label}</span>
                <span className="small muted" style={{ display: 'block' }}>{d.open === 0 ? 'Full' : `${d.open} open`}</span>
              </button>
            ))}
          </div>

          <div className="small muted" style={{ margin: '14px 0 8px' }}>Time of day</div>
          <div className="segmented" style={{ maxWidth: 420, borderRadius: 10 }}>
            {WHEN.map((w) => (
              <button key={w} aria-pressed={partOfDay === w} onClick={() => setPartOfDay(w)} style={{ flex: 1, minHeight: 44 }}>
                {w}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: '16px 18px' }}>
          <div className="small muted" style={{ marginBottom: 8 }}>Available times</div>
          {slots.loading && <span className="small muted">Checking…</span>}
          {!slots.loading && slots.data?.slots.length === 0 && (
            <div className="empty">Nothing open that {partOfDay.toLowerCase()}. Try another part of the day.</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(206px, 100%), 1fr))', gap: 10 }}>
            {slots.data?.slots.map((slot) => {
              const on = picked?.startsAt === slot.startsAt && picked?.aircraftId === slot.aircraftId;
              return (
                <button
                  key={`${slot.startsAt}-${slot.aircraftId}`}
                  onClick={() => setPicked(slot)}
                  style={{
                    padding: '11px 13px', minHeight: 76, textAlign: 'left', borderRadius: 12,
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                    background: on ? 'var(--accent-tint)' : 'var(--surface)',
                    boxShadow: on ? '0 0 0 2px var(--accent-tint)' : 'none',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 600 }}>
                      {new Date(slot.startsAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                    </span>
                    <Pill>{slot.tag === 'Tight turn' ? 'Last opening' : 'Available'}</Pill>
                  </span>
                  <span style={{ display: 'block', fontSize: 12.5, marginTop: 4 }}>{slot.instructorName}</span>
                  <span className="small muted" style={{ display: 'block' }}>{slot.aircraftTail} · {slot.aircraftModel}</span>
                </button>
              );
            })}
          </div>

          <label className="field" style={{ marginTop: 14 }}>
            <span>Note for your instructor (optional)</span>
            <textarea className="textarea" style={{ borderRadius: 10, padding: '10px 11px' }}
              placeholder="Anything you want to work on?" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>

          <p className="small muted" style={{ marginTop: 12, textWrap: 'pretty' }}>
            Requests hold the slot for 12 hours while your instructor confirms. FlightSlots reports
            items that need review \u2014 it never clears a flight.
          </p>
        </div>
      </div>
    </Drawer>
  );
}
